import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users,
  Crown,
  Plus,
  Search,
  Trash2,
  Key,
  Eye,
  CreditCard,
  Settings,
  MoreHorizontal,
  AlertTriangle,
  Bell,
  Info,
  Link2
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { InputVenture } from "@/components/ui/input-venture";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useClients } from "@/hooks/useClients";
import { Client } from "@/types/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddClientModal } from "@/components/admin/AddClientModal";
import { ViewCredentialsModal } from "@/components/admin/ViewCredentialsModal";
import { PaymentMethodsTab } from "@/components/admin/PaymentMethodsTab";
import { IntegrationsManager } from "@/components/admin/IntegrationsManager";
import { ClientIntegrationsTab } from "@/components/admin/ClientIntegrationsTab";
import { FanbasisSetupTab } from "@/components/admin/FanbasisSetupTab";
import { RevenueCollectionTab } from "@/components/admin/RevenueCollectionTab";
import { ClientBillingRow } from "@/components/admin/ClientBillingRow";
import { useClientBillingStates } from "@/hooks/useClientBillingStates";
import { startImpersonation } from "@/utils/clientUtils";
import { canAccessTab, canEditClients, canViewRevenue, getPermissions } from "@/lib/permissions";
import { useRole } from "@/contexts/RoleContext";
import { useSetupStatus } from "@/hooks/useSetupStatus";
import { PageTour } from "@/components/tour/PageTour";
import { commandCenterSteps } from "@/lib/tour-steps";

function ClientSetupDot({ clientId }: { clientId: string }) {
  const s = useSetupStatus(clientId);
  const [pinned, setPinned] = useState(false);

  const adAccountsOk = s.googleAdsConnected || s.metaAdsConnected
    || (s.googleAdsNotNeeded && s.metaAdsNotNeeded);
  const checks = [s.ghlConnected, adAccountsOk, s.fanbasisConnected, s.paymentMethodCount >= 1];
  const passed = checks.filter(Boolean).length;
  const textColor = s.loading
    ? "text-muted-foreground/30"
    : passed === checks.length
      ? "text-green-500"
      : "text-red-500";

  const googleLabel = s.googleAdsNotNeeded ? "Google Ads (not needed)" : "Google Ads";
  const metaLabel = s.metaAdsNotNeeded ? "Meta Ads (not needed)" : "Meta Ads";

  const items = [
    { label: "GHL", ok: s.ghlConnected },
    { label: googleLabel, ok: s.googleAdsConnected || s.googleAdsNotNeeded },
    { label: metaLabel, ok: s.metaAdsConnected || s.metaAdsNotNeeded },
    { label: "Fanbasis", ok: s.fanbasisConnected },
    { label: "Payment Methods", ok: s.paymentMethodCount >= 1 },
    { label: "Stage Mappings", ok: s.stageMappingsConfigured },
  ];

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={pinned || undefined}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={`${textColor} shrink-0 cursor-pointer hover:opacity-80 transition-opacity`}
            onClick={(e) => { e.stopPropagation(); setPinned(!pinned); }}
            onBlur={() => setPinned(false)}
          >
            <Info className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {s.loading ? (
            <p>Checking setup status...</p>
          ) : (
            items.map((item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span className={item.ok ? "text-green-500" : "text-red-500"}>{item.ok ? "✓" : "✗"}</span>
                <span className={item.ok ? "text-green-500" : "text-red-500"}>{item.label}</span>
              </div>
            ))
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function CommandCenter() {
  const { adminSubRole } = useRole();
  const isSuperAdmin = adminSubRole === 'super_admin';
  const {
    clients,
    addClient,
    pauseClient,
    unpauseClient,
    deactivateClient,
    deleteClient,
    updateClient,
    removalRequests,
    updateRemovalRequest,
    getClientStats,
    getPendingRequestsCount,
    upgradeRequests,
    tickets
  } = useClients();
  
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<'active' | 'paused' | 'inactive' | 'all'>('active');
  const [activeTab, setActiveTab] = useState("clients");
  // Incremented on any client mutation (add/delete/update) to signal child tabs to refetch
  const [dataVersion, setDataVersion] = useState(0);
  const bumpVersion = () => setDataVersion(v => v + 1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState<'pause' | 'deactivate' | 'delete'>('deactivate');
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false);
  const [isRequestsModalOpen, setIsRequestsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle GHL OAuth redirect query parameters (?ghl=connected or ?ghl=error)
  useEffect(() => {
    const ghlStatus = searchParams.get('ghl');
    if (ghlStatus === 'connected') {
      toast.success('GoHighLevel Connected', {
        description: 'GHL account connected successfully. Initial sync started.',
      });
    } else if (ghlStatus === 'error') {
      toast.error('Connection Failed', {
        description: 'Could not connect to GoHighLevel. Please try again or check your GHL account permissions.',
      });
    }

    // Clear query parameter from URL without page reload
    if (ghlStatus) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('ghl');
      newParams.delete('reason');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle Google Ads OAuth redirect query parameters (?google_ads=connected or ?google_ads=error)
  useEffect(() => {
    const googleAdsStatus = searchParams.get('google_ads');
    if (googleAdsStatus === 'connected') {
      toast.success('Google Ads Connected', {
        description: 'Google Ads account connected successfully. Initial sync started.',
      });
    } else if (googleAdsStatus === 'error') {
      const reason = searchParams.get('reason');
      toast.error('Google Ads Connection Failed', {
        description: reason
          ? `Could not connect Google Ads: ${reason.replace(/_/g, ' ')}.`
          : 'Could not connect to Google Ads. Please try again.',
      });
    }

    // Clear query parameter from URL without page reload
    if (googleAdsStatus) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('google_ads');
      newParams.delete('reason');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Get user role from localStorage
  const userRole = adminSubRole || 'member';
  const permissions = getPermissions(userRole);

  const stats = getClientStats();
  const pendingRequestsCount = getPendingRequestsCount();
  const pendingUpgrades = upgradeRequests?.filter(r => r.status === 'pending').length || 0;
  const openTickets = tickets?.filter(t => t.status === 'open').length || 0;

  // Calculate MRR from actual charges (only visible to those with revenue access)
  const [totalMRR, setTotalMRR] = useState(0);
  useEffect(() => {
    const fetchChargedRevenue = async () => {
      // Get last 4 weeks of successful charges to calculate monthly run rate
      const fourWeeksAgo = new Date();
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      const { data } = await supabase
        .from('weekly_billing')
        .select('total_amount_cents')
        .eq('fanbasis_status', 'succeeded')
        .gte('billing_week_start', fourWeeksAgo.toISOString().split('T')[0]);
      const totalCents = (data || []).reduce((sum, r) => sum + (r.total_amount_cents || 0), 0);
      setTotalMRR(Math.round(totalCents / 100));
    };
    fetchChargedRevenue();
  }, [dataVersion]);

  // Phase 11 Plan 05: billing state per client (D-14/D-16)
  const { data: billingStates } = useClientBillingStates();

  const pausedCount = clients.filter(c => c.status === 'paused').length;
  const inactiveCount = clients.filter(c => c.status === 'inactive').length;

  const filteredClients = clients
    .filter(c => statusFilter === 'all' || c.status === statusFilter)
    .filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase())
    );

  const handleAddClient = (clientData: Omit<Client, "id">) => {
    addClient(clientData);
    bumpVersion();
  };

  const handleLinkFanbasis = async (client: Client) => {
    try {
      toast.info(`Looking up Fanbasis customer for ${client.name}...`);
      const { data, error } = await supabase.functions.invoke('fanbasis-lookup-customer', {
        body: { clientId: client.id },
      });
      if (error) throw error;
      if (data?.alreadyLinked) {
        toast.success(`${client.name} is already linked to Fanbasis`);
      } else if (data?.success) {
        toast.success(`Linked ${client.name} to Fanbasis customer ${data.customerEmail}`);
        setDataVersion(v => v + 1);
      } else {
        toast.error(data?.error || `No Fanbasis customer found for ${client.name}`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to link Fanbasis customer');
    }
  };

  const handleGeneratePaymentLink = async (client: Client) => {
    try {
      toast.info(`Generating payment link for ${client.name}...`);
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { client_id: client.id },
      });
      if (error || !data?.payment_link) {
        let detail = 'Failed to generate payment link';
        try {
          const ctx = (error as any)?.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            detail = body?.error || body?.detail || detail;
          } else if (error instanceof Error) {
            detail = error.message;
          }
        } catch {}
        toast.error(detail);
        return;
      }
      window.open(data.payment_link, '_blank');
      toast.success(`Payment link generated for ${client.name}`);
      setDataVersion(v => v + 1); // refresh client list to pick up the new fanbasis_payment_link
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate payment link');
    }
  };

  const handleClientAction = async () => {
    if (!selectedClient) return;
    try {
      switch (deleteAction) {
        case 'pause':
          pauseClient(selectedClient.id);
          toast.success(`${selectedClient.name} paused`);
          break;
        case 'deactivate':
          deactivateClient(selectedClient.id);
          toast.success(`${selectedClient.name} deactivated`);
          break;
        case 'delete':
          await deleteClient(selectedClient.id);
          toast.success(`${selectedClient.name} permanently deleted`);
          break;
      }
      bumpVersion();
    } catch (error: unknown) {
      toast.error('Delete failed', {
        description: error instanceof Error ? error.message : 'Failed to delete client',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedClient(null);
    }
  };

  const handleViewDashboard = (client: Client) => {
    startImpersonation(client.id, client.name, client.package, client.services);
  };

  const handleIntegrationToggle = (integration: 'ghl' | 'metaAds' | 'googleAds') => {
    if (!selectedClient) return;
    
    const currentStatus = selectedClient.integrations[integration];
    if (!currentStatus) {
      const confirmed = window.confirm(`Connect to ${
        integration === 'ghl' ? 'GoHighLevel' : 
        integration === 'metaAds' ? 'Meta Ads' : 'Google Ads'
      }? (This will redirect to ${integration} login)`);
      
      if (confirmed) {
        updateClient(selectedClient.id, {
          integrations: { ...selectedClient.integrations, [integration]: true }
        });
        setSelectedClient({
          ...selectedClient,
          integrations: { ...selectedClient.integrations, [integration]: true }
        });
      }
    } else {
      updateClient(selectedClient.id, {
        integrations: { ...selectedClient.integrations, [integration]: false }
      });
      setSelectedClient({
        ...selectedClient,
        integrations: { ...selectedClient.integrations, [integration]: false }
      });
    }
  };

  const pendingRequests = removalRequests.filter(r => r.status === 'pending');

  // Build stat cards based on permissions
  const statCards = [
    { label: "Active Clients", value: stats.active, icon: Users, color: "text-secondary", show: true, tooltip: "Clients with active status in the system" },
    { label: "Total MRR", value: `$${totalMRR.toLocaleString()}`, icon: CreditCard, color: "text-green-400", show: canViewRevenue(userRole), tooltip: "Sum of successful Fanbasis charges in the last 28 days" },
    { label: "Pending Tickets", value: openTickets, icon: AlertTriangle, color: "text-amber-400", show: true, tooltip: null as string | null },
    { label: "Upgrade Requests", value: pendingUpgrades, icon: Crown, color: "text-purple-400", show: true, tooltip: null as string | null },
  ].filter(stat => stat.show);

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={commandCenterSteps} tourId="command-center" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Command Center</h1>
          <p className="text-muted-foreground">Manage all clients and operations</p>
        </div>
        <div className="flex gap-2">
          {pendingRequestsCount > 0 && (
            <Button 
              variant="ghost" 
              className="relative"
              onClick={() => setIsRequestsModalOpen(true)}
            >
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {pendingRequestsCount}
              </span>
            </Button>
          )}
          {canEditClients(userRole) && (
            <Button onClick={() => setIsAddModalOpen(true)} data-tour-id="add-client">
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div data-tour-id="stat-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <VentureCard>
              <VentureCardContent className="flex items-center gap-4">
                <div className={`p-3 rounded-lg bg-surface ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">
                    {stat.label}
                    {stat.tooltip && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{stat.tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </p>
                </div>
              </VentureCardContent>
            </VentureCard>
          </motion.div>
        ))}
      </div>

      {/* Tabs - Role-based visibility */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-tour-id="tabs">
        <TabsList className="flex-wrap">
          {canAccessTab(userRole, 'client_registry') && (
            <TabsTrigger value="clients">Client Registry</TabsTrigger>
          )}
          {canAccessTab(userRole, 'client_integrations') && (
            <TabsTrigger value="integrations">Client Integrations</TabsTrigger>
          )}
          {canAccessTab(userRole, 'payment_methods') && (
            <TabsTrigger value="payments">Payment Methods</TabsTrigger>
          )}
          {canAccessTab(userRole, 'payment_methods') && (
            <TabsTrigger value="revenue">Revenue Collection</TabsTrigger>
          )}
          {canAccessTab(userRole, 'fanbasis_setup') && (
            <TabsTrigger value="fanbasis">Fanbasis Setup</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="clients" className="mt-6">
          {/* Client Registry */}
          <VentureCard>
            <VentureCardHeader className="flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <VentureCardTitle>Client Registry</VentureCardTitle>
              <div className="flex items-center gap-3">
                {isSuperAdmin && (
                  <div className="flex gap-1 text-xs">
                    {(['active', 'paused', 'inactive', 'all'] as const).map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-2.5 py-1 rounded-md capitalize transition-colors ${
                          statusFilter === s
                            ? s === 'paused' ? 'bg-yellow-500/20 text-yellow-400'
                            : s === 'inactive' ? 'bg-orange-500/20 text-orange-400'
                            : 'bg-secondary/20 text-secondary'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {s}{s === 'paused' && pausedCount > 0 ? ` (${pausedCount})` : s === 'inactive' && inactiveCount > 0 ? ` (${inactiveCount})` : ''}
                      </button>
                    ))}
                  </div>
                )}
                <div className="w-full sm:w-64" data-tour-id="client-search">
                  <InputVenture
                    icon={<Search className="w-4 h-4" />}
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </VentureCardHeader>
            <VentureCardContent>
              {filteredClients.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {statusFilter === 'paused' ? 'No paused clients' :
                     statusFilter === 'inactive' ? 'No inactive clients' :
                     statusFilter === 'all' ? 'No clients found' :
                     'No clients yet'}
                  </p>
                  {canEditClients(userRole) && statusFilter === 'active' && (
                    <>
                      <p className="text-sm text-muted-foreground mb-4">Click 'Add Client' to get started.</p>
                      <Button onClick={() => setIsAddModalOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Client
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto" data-tour-id="client-table">
                  <table className="w-full min-w-[700px]">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Package</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Services</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client Code</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Billing</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        {canEditClients(userRole) && (
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map((client) => (
                        <tr 
                          key={client.id} 
                          className="border-b border-border hover:bg-surface transition-colors"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <ClientSetupDot clientId={client.id} />
                              <div>
                                <p className="font-medium">{client.name}</p>
                                <p className="text-sm text-muted-foreground">{client.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1.5">
                              <BadgeVenture variant={client.package === 'elite' ? 'warning' : 'info'}>
                                {client.package}
                              </BadgeVenture>
                              {client.status === 'paused' && (
                                <BadgeVenture variant="warning">paused</BadgeVenture>
                              )}
                              {client.status === 'inactive' && (
                                <BadgeVenture variant="error">inactive</BadgeVenture>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              {client.services.demos && <BadgeVenture variant="default">Demos</BadgeVenture>}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono text-sm text-secondary">
                              {client.credentials?.clientCode || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {billingStates?.get(client.id) ? (
                              <ClientBillingRow
                                clientId={client.id}
                                billing_mode={billingStates.get(client.id)!.billing_mode}
                                subscription_status={billingStates.get(client.id)!.subscription_status}
                                has_payment_method={billingStates.get(client.id)!.has_payment_method}
                                has_unresolved_dispute={billingStates.get(client.id)!.has_unresolved_dispute}
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <BadgeVenture variant={client.status === 'active' ? 'success' : 'error'}>
                              {client.status}
                            </BadgeVenture>
                          </td>
                          {canEditClients(userRole) && (
                            <td className="py-3 px-4">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-surface border-border">
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedClient(client);
                                    setIsCredentialsModalOpen(true);
                                  }}>
                                    <Key className="w-4 h-4 mr-2" />
                                    View Credentials
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleViewDashboard(client)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Dashboard
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setActiveTab('payments');
                                  }}>
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Payment Method
                                  </DropdownMenuItem>
                                  {!client.fanbasisPaymentLink && (
                                    <DropdownMenuItem onClick={() => handleGeneratePaymentLink(client)}>
                                      <CreditCard className="w-4 h-4 mr-2" />
                                      Generate Payment Link
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => handleLinkFanbasis(client)}>
                                    <Link2 className="w-4 h-4 mr-2" />
                                    Link Fanbasis
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    setSelectedClient(client);
                                    setIsIntegrationsModalOpen(true);
                                  }}>
                                    <Settings className="w-4 h-4 mr-2" />
                                    Integrations
                                  </DropdownMenuItem>
                                  {isSuperAdmin && (
                                    <>
                                      <DropdownMenuSeparator />
                                      {client.status === 'paused' ? (
                                        <DropdownMenuItem onClick={() => {
                                          unpauseClient(client.id);
                                          toast.success(`${client.name} reactivated`);
                                        }}>
                                          <Eye className="w-4 h-4 mr-2" />
                                          Unpause Client
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem
                                          className="text-yellow-400 focus:text-yellow-300"
                                          onClick={() => {
                                            setSelectedClient(client);
                                            setDeleteAction('pause');
                                            setIsDeleteDialogOpen(true);
                                          }}
                                        >
                                          <AlertTriangle className="w-4 h-4 mr-2" />
                                          Pause Client
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem
                                        className="text-orange-400 focus:text-orange-300"
                                        onClick={() => {
                                          setSelectedClient(client);
                                          setDeleteAction('deactivate');
                                          setIsDeleteDialogOpen(true);
                                        }}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Deactivate Client
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        className="text-red-400 focus:text-red-300"
                                        onClick={() => {
                                          setSelectedClient(client);
                                          setDeleteAction('delete');
                                          setIsDeleteDialogOpen(true);
                                        }}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Permanently Delete
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </VentureCardContent>
          </VentureCard>
        </TabsContent>

        <TabsContent value="integrations" className="mt-6">
          <ClientIntegrationsTab dataVersion={dataVersion} />
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <PaymentMethodsTab dataVersion={dataVersion} />
        </TabsContent>

        <TabsContent value="revenue" className="mt-6">
          <RevenueCollectionTab dataVersion={dataVersion} />
        </TabsContent>

        <TabsContent value="fanbasis" className="mt-6">
          <FanbasisSetupTab />
        </TabsContent>
      </Tabs>

      {/* Add Client Modal */}
      <AddClientModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onAddClient={handleAddClient}
      />

      {/* View Credentials Modal */}
      <ViewCredentialsModal
        open={isCredentialsModalOpen}
        onOpenChange={setIsCredentialsModalOpen}
        client={selectedClient}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${deleteAction === 'delete' ? 'text-red-400' : deleteAction === 'deactivate' ? 'text-orange-400' : 'text-yellow-400'}`} />
              {deleteAction === 'pause' && 'Pause Client'}
              {deleteAction === 'deactivate' && 'Deactivate Client'}
              {deleteAction === 'delete' && 'Permanently Delete Client'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteAction === 'pause' && (
                <>Pause <strong>{selectedClient?.name}</strong>? They won't be able to log in until unpaused. All data is preserved.</>
              )}
              {deleteAction === 'deactivate' && (
                <>Deactivate <strong>{selectedClient?.name}</strong>? They won't be able to log in. All data is preserved for billing records. This can be reversed by changing their status in the database.</>
              )}
              {deleteAction === 'delete' && (
                <>Are you sure you want to permanently delete <strong>{selectedClient?.name}</strong> and ALL their data? This includes demos, billing history, payment methods, and integrations. <strong>This cannot be undone.</strong></>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClientAction}
              className={deleteAction === 'delete' ? 'bg-red-500 hover:bg-red-600' : deleteAction === 'deactivate' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-yellow-500 hover:bg-yellow-600 text-black'}
            >
              {deleteAction === 'pause' && 'Pause Client'}
              {deleteAction === 'deactivate' && 'Deactivate'}
              {deleteAction === 'delete' && 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Integrations Modal */}
      <Dialog open={isIntegrationsModalOpen} onOpenChange={setIsIntegrationsModalOpen}>
        <DialogContent className="bg-surface border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle>Integrations - {selectedClient?.name}</DialogTitle>
            <DialogDescription>Manage connected platforms for this client.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {selectedClient && (
              <IntegrationsManager 
                client={selectedClient} 
                onUpdate={(id, updates) => {
                  updateClient(id, updates);
                  setSelectedClient({ ...selectedClient, ...updates });
                }} 
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsIntegrationsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Removal Requests Modal */}
      <Dialog open={isRequestsModalOpen} onOpenChange={setIsRequestsModalOpen}>
        <DialogContent className="bg-surface border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle>Closer Removal Requests</DialogTitle>
            <DialogDescription>Review and manage client requests to remove closers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
            {pendingRequests.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No pending requests</p>
            ) : (
              pendingRequests.map((request) => (
                <div key={request.id} className="p-4 rounded-lg border border-border bg-muted/20">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{request.clientName}</p>
                      <p className="text-sm text-muted-foreground">Closer: {request.closerName}</p>
                      <p className="text-sm text-muted-foreground mt-1">"{request.reason}"</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateRemovalRequest(request.id, 'dismissed')}
                      >
                        Dismiss
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateRemovalRequest(request.id, 'approved')}
                      >
                        Approve
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsRequestsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
